import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const realUser = ref(null)
  const loading = ref(false)
  const isInitialized = ref(false)
  let _initPromise = null

  const isLoggedIn = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.admin === true)
  const isRealAdmin = computed(() => (realUser.value ?? user.value)?.admin === true)
  const isImpersonating = computed(() => !!realUser.value)

  // True when a non-admin user has no email address on file yet.
  // Components can watch this to trigger an onboarding prompt.
  const needsOnboarding = computed(() =>
    isLoggedIn.value && !isImpersonating.value && user.value?.hasEmails === false
  )

  async function fetchMe() {
    loading.value = true
    try {
      const res = await authApi.me()
      user.value = res.data
      realUser.value = res.data.realUser ?? null
    } catch {
      user.value = null
      realUser.value = null
    } finally {
      loading.value = false
      isInitialized.value = true
    }
  }

  // Router guard calls this before making auth decisions. De-duplicates the
  // in-flight request so the guard and main.js share the same Promise.
  function ensureInitialized() {
    if (isInitialized.value) return Promise.resolve()
    if (_initPromise) return _initPromise
    _initPromise = fetchMe()
    return _initPromise
  }

  function applyLoginResponse(res) {
    user.value = res.data
    realUser.value = res.data.realUser ?? null
    // Mark as initialized so the router guard doesn't make a redundant /auth/me
    // call immediately after login — which would create an unnecessary failure window.
    isInitialized.value = true
    _initPromise = null
  }

  async function login(token) {
    applyLoginResponse(await authApi.login(token))
  }

  async function emailLogin(token) {
    applyLoginResponse(await authApi.emailLogin(token))
  }

  async function requestEmailLogin(email) {
    await authApi.requestEmailLogin(email)
  }

  async function logout() {
    await authApi.logout()
    user.value = null
    realUser.value = null
    window.location.replace('/login')
  }

  async function impersonate(userId) {
    await authApi.impersonate(userId)
    await fetchMe()
  }

  async function stopImpersonating() {
    await authApi.stopImpersonating()
    await fetchMe()
  }

  function clearUser() {
    user.value = null
    realUser.value = null
  }

  return {
    user, realUser, loading, isInitialized,
    isLoggedIn, isAdmin, isRealAdmin, isImpersonating, needsOnboarding,
    fetchMe, ensureInitialized, login, emailLogin, requestEmailLogin, logout, impersonate, stopImpersonating, clearUser
  }
})
